import { z } from "zod";
import { parseOrThrow } from '_shared/_utilities/zod-schema-parser';
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import MessageSubject from "notifications/values/message-subject/message-subject";

export type MessageSubjectJSON = z.infer<typeof MessageSubjectSchema.MessageSubjectJSONZ>;
export type MessageSubjectParams = z.infer<typeof MessageSubjectSchema.MessageSubjectZ>;

export default class MessageSubjectSchema {
  public static readonly MessageSubjectJSONZ = z.string();

  public static readonly MessageSubjectZ = z.custom<MessageSubject>(
    (data): data is MessageSubject => data != undefined
      && typeof data === "object"
      && 'value' in data
      && typeof data.value === 'string'
      && data.value?.trim().length > 0,
    {
      message: "MessageSubject value must be a non-empty string.",
    }
  );

  public static validate(values: unknown): MessageSubjectParams {
    return parseOrThrow(
      MessageSubjectSchema.MessageSubjectZ,
      values,
      { 
        logError: true,
        schemaName: "MessageSubjectSchema",
        operation: "validate",
        payloadSample: values 
      }
    );
  }

  public static validateJSON(values: unknown): MessageSubjectJSON {
    return parseOrThrow(
      MessageSubjectSchema.MessageSubjectJSONZ,
      values,
      { 
        logError: true,
        schemaName: "MessageSubjectSchema",
        operation: "validateJSON",
        payloadSample: values 
      }
    );
  }

  public static isValid(values: unknown): values is MessageSubjectParams {
    try {
      MessageSubjectSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is MessageSubjectJSON {
    try {
      MessageSubjectSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: string | undefined): MessageSubjectParams | undefined {
    if (isNullOrUndefined(data)) return data;
    const parsed = parseOrThrow(
      MessageSubjectSchema.MessageSubjectJSONZ,
      data,
      { 
        logError: true,
        schemaName: "MessageSubjectSchema",
        operation: "deserialize",
        payloadSample: data 
      }
    );
    return new MessageSubject(parsed);
  }

  public static serialize(subject: MessageSubjectParams | null | undefined): MessageSubjectJSON | null | undefined {
    if (isNullOrUndefined(subject)) return subject;
    const parsed = MessageSubjectSchema.validate(subject);
    return parsed.value;
  }
}
